import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import type {
  EnsureInitialRoomInventoryOrganization,
  EnsureInitialRoomInventoryRoom
} from '#~/api/agent/registry.js';

/**
 * Auto-link state machine states.
 *
 * Flow: IDLE → CHECKING → LINKING → VERIFYING_LINK → SUCCESS
 *              ↓            ↓            ↓
 *            ERROR        ERROR        ERROR
 *              ↓
 *     INVENTORY_CHANGED (Root refreshes and re-decides one/many entry)
 */
export type AutoLinkState =
  | { readonly status: 'idle' }
  | { readonly status: 'checking'; readonly startedAt: number }
  | {
      readonly status: 'linking';
      readonly bridgeId: Agent['id'];
      readonly roomId: Room['id'];
    }
  | {
      readonly status: 'verifying_link';
      readonly bridgeId: Agent['id'];
      readonly roomId: Room['id'];
      readonly linkedAt: number;
    }
  | {
      readonly status: 'success';
      readonly roomId: Room['id'];
      readonly room?: EnsureInitialRoomInventoryRoom;
      readonly ownedOrganizations?: EnsureInitialRoomInventoryOrganization[];
    }
  | {
      readonly status: 'inventory_changed';
      readonly roomCount: number;
      /** Primary-backed identities are present when the atomic ensure lost. */
      readonly rooms?: EnsureInitialRoomInventoryRoom[];
      readonly ownedOrganizations?: EnsureInitialRoomInventoryOrganization[];
    }
  | {
      readonly status: 'error';
      readonly reason: AutoLinkError;
      readonly message: string;
    }
  | { readonly status: 'skipped'; readonly reason: SkipReason };

export type AutoLinkError =
  | 'not_electron'
  | 'not_iris_vendor'
  | 'ipc_failed'
  | 'server_check_failed'
  | 'bridge_link_failed'
  | 'timeout'
  // ENG-4154 warm account-switch: the sidecar re-confirmed a link the current
  // account's registry definitively disowns (its boot-time cloud validation
  // predates a same-window logout → login). The controller unlinked the
  // sidecar; the sidecar's unlink flow reboots the app, which reconverges
  // through the fresh-install link path.
  | 'stale_link_cleared'
  // ENG-4166 D2 cold-boot deadlock: the bridge is linked to a room the CURRENT
  // token cannot access (a cross-account / forbidden link). Auto-link `success`
  // must imply room access, so a definitive-forbidden linked room is a terminal
  // here — never a false `success` that dooms the boot. The renderer cannot
  // clear the backend link in place (bridge:unlink reboots, and bridge:reboot is
  // suppressed under IRIS_E2E_TEST), so root bootstrap routes this terminal to
  // the boot-error screen (D3) rather than spinning on an inaccessible room.
  | 'cross_account_link'
  // Sidecar silence/error/timeout while a completion record still proves a
  // prior link. Never createRoom; the existing error overlay + user restart
  // is the recovery. Only a definitive live sidecar `unlinked` may clear the
  // record.
  | 'bridge_unhealthy';

export type SkipReason =
  | 'not_electron'
  | 'not_iris_vendor'
  | 'attempts_exhausted'
  | 'network_unavailable'
  // Root settled an authoritative nonzero inventory, so auto-link is not part
  // of this account bootstrap branch.
  | 'account_has_rooms'
  // Registry lookup degraded (failed/timed out) but the local identity store
  // reports the agent linked — local truth outranks a degraded network read,
  // so the machine skips instead of re-linking a linked agent (ENG-4154).
  | 'already_linked_local';

export type AutoLinkStatus = AutoLinkState['status'];

export interface AutoLinkContext {
  readonly getToken: () => Promise<string>;
  readonly userId: string;
  readonly hostname: string | undefined;
  readonly isElectron: boolean;
  readonly vendor: string | undefined;
}

export interface AutoLinkControllerOptions {
  readonly ipcTimeoutMs?: number;
  readonly bridgeOnlineTimeoutMs?: number;
  /** Deadline for the checking-phase registry lookups (linked-room checks). */
  readonly serverCheckTimeoutMs?: number;
  /**
   * Grace before the live sidecar re-consult that guards the
   * `already_linked_local` skip on a DEFINITIVE registry not-linked answer
   * (see AutoLinkController.reconfirmSidecarLinkLive). Injectable so tests
   * don't wall-clock wait.
   */
  readonly sidecarReconfirmGraceMs?: number;
  /**
   * Grace before the SINGLE re-probe of agents/get that disambiguates a
   * provisional cross-account 403 from a transient fresh-link 403 on refresh
   * (see AutoLinkController, the staleCrossAccount re-probe). A 403 that is
   * eventual-consistency lag (a per-run-org link minted ms ago) flips to a
   * readable 200 within seconds as the registry replica converges; a genuine
   * cross-account 403 is a stable policy fact and stays forbidden across the
   * window. Injectable so tests don't wall-clock wait. Defaults to
   * DEFAULT_STALE_REVALIDATE_GRACE_MS (same 2s window as
   * sidecarReconfirmGraceMs — both size the same eventual-consistency lag).
   */
  readonly staleRevalidateGraceMs?: number;
  readonly onStateChange?: (state: AutoLinkState) => void;
  readonly onNonFatalError?: (kind: 'persistence_write_failed', error: unknown) => void;
}

// Timeouts for the auto-link state machine's IPC and bridge-online waits.
//
// Each call against the Electron main process has its OWN per-call ceiling
// here; the bridge itself polls auth indefinitely (see bridge_ipc/bridge.ts).
// These values are the user-facing "we'll wait this long before showing
// the manual-link fallback UI" — so they reflect realistic worst-case cold
// starts (slow disk auth-sidecar bootstrap, signature verification, fault
// injection in test sweeps) rather than fast-path optimism.
//
// History: the originals were 10s/17s, sized for a hot-path local dev loop.
// They pre-empted real cold-start auth in the AUTH_DELAY_START_MS sweep
// (every cell at AUTH_DELAY>=20000 hit `[auto-link] checking → error`
// exactly when these timeouts fired). 300s (5 min) covers the documented
// mechanical chain — auth sleep up to 180s in fault injection +
// bridge:linkv2 (~20s) + linker → newts → auth token store (~7s after
// linkv2) — with margin. In production, auth boots in <5s so this ceiling
// is never hit; in a sweep, the AUTH_DELAY=180000 cell completes at ~187s,
// well below 300s.
export const DEFAULT_IPC_TIMEOUT_MS = 300_000;
export const DEFAULT_BRIDGE_ONLINE_TIMEOUT_MS = 300_000;
// Deadline for the checking-phase registry lookups (checkBridgeLinkedRoom).
// These are direct Lambda reads — a cold start is ~10s worst case (knowledge:
// auto-link-prewarm-decode-error-classification) — so 15s gives margin while
// staying far inside the e2e boot budgets. A timeout degrades to "not linked"
// (the 404/403 answer), letting the machine continue instead of wedging in
// `checking` on a hung fetch (ENG-4034 bug class; CI run 27310429937).
export const DEFAULT_SERVER_CHECK_TIMEOUT_MS = 15_000;
// Grace before the live sidecar re-consult when a DEFINITIVE registry
// not-linked answer contradicts a sidecar-reported link. Sized to the
// observed race: the sidecar's boot validation wiped the stale triple ~65ms
// after serving it, landing within ~1s of sidecar boot (CI run 27332628785,
// win delay-amplification CV scenario) — 2s covers it with margin while
// costing nothing on the happy path (the re-consult only runs when registry
// and sidecar disagree, already an exceptional situation).
export const DEFAULT_SIDECAR_RECONFIRM_GRACE_MS = 2_000;
// Grace before the SINGLE agents/get re-probe that distinguishes a transient
// fresh-link 403 (per-run-org link minted ms earlier, eventual-consistency
// lag — flips to 200 within seconds) from a SUSTAINED cross-account 403 (a
// stable policy fact). On a renderer refresh the full auto-link flow re-runs;
// for a just-linked agent checkBridgeLinkedRoom can read a transient 403 and
// the controller would otherwise misclassify it as cross-account and unlink
// (ENG-4154 path), which under IRIS_E2E_TEST dead-ends the loader (bridge:reboot
// suppressed, ipc.ts ~826) → B2 "Unable to connect" → 30s refresh-budget
// timeout. 2s lets the replica converge while costing nothing on the happy path
// (the re-probe runs only on the already-narrow suspected-stale path). Mirrors
// DEFAULT_SIDECAR_RECONFIRM_GRACE_MS sizing — same eventual-consistency window.
export const DEFAULT_STALE_REVALIDATE_GRACE_MS = 2_000;
export const AUTO_LINK_VERSION = '2.0';
export const PERSISTENCE_KEY_COMPLETED = 'auto_link_v2_completed';
